/**
 * Issue #121 — Windows 管理端桌面壳的运行期配置解析层。
 *
 * 同一套 apps/admin 渲染层既服务浏览器，也被 Electron 桌面壳直接复用，差异只有两点：
 *   1. 后端地址从哪里来：
 *      - 浏览器 / Web：相对 `/api/v1`，由 Vite dev server / Nginx 同源代理转发。
 *      - 桌面壳：主进程经预加载脚本把绝对 `apiBaseUrl` 注入到
 *        `window.__JZ_ADMIN_RUNTIME__`（来自统一发布元数据 + 可选 env 覆盖）。
 *   2. 网络请求怎么发：
 *      - 浏览器：标准 `fetch`，同源。
 *      - 桌面壳：通过一条走主进程 `net.fetch` 的 IPC 传输发出。这样自定义协议
 *        来源（`jzadmin://`）无需后端配置 CORS，且 Bearer 令牌只在受信任的主
 *        进程网络链路里流转，不暴露给任意网页来源。
 *
 * 本模块为纯函数 + 防御式读取：没有桌面桥时一切回退到 Web 默认，行为与既有
 * 实现完全一致（便于单测、避免给 Web 端引入回归）。
 */

/** 桌面 IPC 传输的请求载荷（仅可序列化原始类型，跨 contextBridge 安全传递）。 */
export interface DesktopFetchRequest {
  url: string;
  method: string;
  headers: [string, string][];
  body?: string;
}

/** 桌面 IPC 传输的响应载荷。`ok` 表示 IPC/网络链路是否成功拿到 HTTP 响应，
 *  与 HTTP 状态码无关（4xx/5xx 仍是 `ok: true`，交由 ApiClient 按状态处理）。 */
export interface DesktopFetchResponse {
  ok: boolean;
  status: number;
  statusText: string;
  headers: [string, string][];
  body: string;
  error?: string;
}

/** 主进程经预加载脚本注入的桌面运行期桥。 */
export interface AdminDesktopRuntime {
  desktop: true;
  appVersion?: string;
  apiBaseUrl?: string;
  invoke: (req: DesktopFetchRequest) => Promise<DesktopFetchResponse>;
}

const RUNTIME_GLOBAL_KEY = '__JZ_ADMIN_RUNTIME__';

/** HTTP 语义上不允许携带响应体的状态码；用其构造 `Response` 必须传 `null` body。 */
const NULL_BODY_STATUS = new Set([101, 103, 204, 205, 304]);

/**
 * 读取并校验桌面运行期桥。形状不合法（缺 invoke / desktop 非 true）一律视为
 * 非桌面环境返回 null，避免半初始化的全局把 Web 端带偏。
 */
export function getDesktopRuntime(): AdminDesktopRuntime | null {
  if (typeof globalThis === 'undefined') return null;
  const raw = (globalThis as Record<string, unknown>)[RUNTIME_GLOBAL_KEY];
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<AdminDesktopRuntime>;
  if (candidate.desktop !== true) return null;
  if (typeof candidate.invoke !== 'function') return null;
  return candidate as AdminDesktopRuntime;
}

/**
 * 解析 ApiClient 应使用的 base URL，优先级：
 *   1. 桌面壳注入的绝对 `apiBaseUrl`
 *   2. 构建期 `VITE_API_BASE_URL`（Web 端如需指向跨域后端时使用）
 *   3. 默认相对 `/api/v1`（同源代理）
 */
export function resolveApiBaseUrl(
  runtime: AdminDesktopRuntime | null = getDesktopRuntime(),
): string {
  if (runtime?.apiBaseUrl && runtime.apiBaseUrl.trim()) {
    return runtime.apiBaseUrl.trim().replace(/\/+$/, '');
  }
  const envBase = readViteApiBaseUrl();
  if (envBase) return envBase.replace(/\/+$/, '');
  return '/api/v1';
}

function readViteApiBaseUrl(): string | undefined {
  // import.meta.env 在 Web 构建下由 Vite 提供；在 node --test 等非 Vite 宿主下不存在。
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  const value = env?.VITE_API_BASE_URL;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function headerEntries(headers: HeadersInit | undefined): [string, string][] {
  if (!headers) return [];
  if (headers instanceof Headers) return Array.from(headers.entries());
  if (Array.isArray(headers)) return headers.map(([k, v]) => [k, v] as [string, string]);
  return Object.entries(headers).map(([k, v]) => [k, String(v)] as [string, string]);
}

/**
 * 构造一个 `fetch` 兼容实现，把请求经桌面 IPC 桥转交主进程 `net.fetch` 发出，
 * 再把可序列化的响应载荷还原成真实 `Response`，从而无缝复用既有 ApiClient
 * 的 ok/content-type/envelope 解析逻辑。
 *
 * ApiClient 只会发出字符串 body（JSON.stringify）或无 body，无需处理流 / FormData。
 */
export function createDesktopFetch(runtime: AdminDesktopRuntime): typeof fetch {
  const desktopFetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? 'GET').toUpperCase();
    const headers = headerEntries(init?.headers);
    const body = typeof init?.body === 'string' ? init.body : undefined;

    const res = await runtime.invoke({ url, method, headers, body });
    if (!res.ok) {
      throw new Error(res.error || `桌面网络链路失败（${url}）`);
    }
    return new Response(NULL_BODY_STATUS.has(res.status) ? null : res.body, {
      status: res.status,
      statusText: res.statusText,
      headers: res.headers,
    });
  };
  return desktopFetch as typeof fetch;
}
